import { getSaiTerminology } from '../../../config/terminology'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import StaffDetails from './StaffDetails'
import MessagingCenter from './MessagingCenter'
import QuoteDetails from './QuoteDetails'
import CreateClaim from './CreateClaim'
import PolicyInfo from './PolicyInfo'
import SideBar from '../../SideBar'
import Dashboard from './Dashboard'
import Holidays from './Holidays'
import Accounts from './Accounts'
import Claims from './Claims'
import EditClaim from './Components/EditClaim'
import ActionClaim from './Components/ActionClaim'
import Quotes from './Quotes'

const SAIContainer = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const { claimId } = useParams()
    const [loadedClaim, setLoadedClaim] = useState(null)
    const actioningClaim = claimId && location.pathname.startsWith('/sai/claims/action/')
    const editingClaim = claimId && !actioningClaim
    const creatingClaim = location.pathname === '/sai/claims/new'
    const claimFormPage = editingClaim || creatingClaim || actioningClaim
    const claimReference = String(loadedClaim?.claim_id) === claimId
        ? loadedClaim.claim_reference
        : String(location.state?.claim?.claim_id) === claimId
            ? location.state.claim.claim_reference
            : '' 
    
    const terms = getSaiTerminology(JSON.parse(localStorage.getItem('policyData')))

    const handleNavigate = (path, options = {}) => {
        if (options.external) {
            window.open(path, '_blank', 'noopener,noreferrer')
            return
        }

        navigate(path)
    }

    const renderContent = () => {
        const { pathname } = location

        if (actioningClaim) {
            return <ActionClaim
                key={claimId}
                claimId={claimId}
                policy={JSON.parse(localStorage.getItem('policyData')) || {}}
                initialClaim={location.state?.claim}
            />
        }

        if (editingClaim) {
            return <EditClaim
                key={claimId}
                open={true}
                claim={{ claim_id: claimId }}
                policy={JSON.parse(localStorage.getItem('policyData')) || {}}
                onClaimLoaded={setLoadedClaim}
            />
        }

        switch (pathname) {
            case '/sai/dashboard':
                return <Dashboard onNavigate={handleNavigate} />
            case '/sai/claims':
                return <Claims />
            case '/sai/claims/new':
                return <CreateClaim />
            case '/sai/staff-details':
                return <StaffDetails />
            case '/sai/holidays':
                return <Holidays />
            case '/sai/accounts':
                return <Accounts />
            case '/sai/messages':
                return <MessagingCenter />
            case '/sai/policy-information':
                return <PolicyInfo />
            case '/sai/quotes':
                return <Quotes />
            default:
                if (pathname.startsWith('/sai/quote-details/')) {
                    return <QuoteDetails />
                }

                return <Dashboard onNavigate={handleNavigate} />
        }
    }

    const getPageHeader = () => {
        const { pathname } = location

        if (actioningClaim) {
            return { title: `Action Claim ${claimReference || ''}`.trim(), subtitle: 'Review and action your claim.' }
        }

        if (editingClaim) {
            return {
                title: `Edit Claim ${claimReference || ''}`.trim(),
                subtitle: 'Review and update your claim details.',
            }
        }

        if (creatingClaim) {
            return {
                title: `Create Claim ${claimReference || ''}`.trim(),
                subtitle: 'Create and Submit your new claim',
            }
        }

        if (pathname.startsWith('/sai/quote-details/')) {
            return {
                title: 'Your new quotes are here.',
                subtitle: 'Please review your quotes for the 2025-2026 academic year.',
            }
        }

        switch (pathname) {
            case '/sai/claims':
                return {
                    title: `View and Log ${terms.claim.pluralTitle}`,
                    subtitle: `Welcome to your Staff Absence Portal. Here you can manage your ${terms.policy.singularLower} and administer ${terms.claim.pluralLower}.`,
                }
            case '/sai/staff-details':
                return {
                    title: 'Manage your Staff Details Here',
                    subtitle: 'Welcome to your Staff Absence Portal. Here you can manage your staff details.',
                }
            case '/sai/holidays':
                return {
                    title: 'Manage your Holidays',
                    subtitle: 'Welcome to your Staff Absence Portal. Here you can manage your school holidays.',
                }
            case '/sai/accounts':
                return {
                    title: `Manage your Staff Absence ${terms.policy.singularTitle}`,
                    subtitle: 'Welcome to your Staff Absence Portal. Here you can manage your account details.',
                }
            case '/sai/messages':
                return {
                    title: 'Messaging Center',
                    subtitle: 'Welcome to your Staff Absence Portal. Here you can keep up to date with your communications.',
                }
            case '/sai/policy-information':
                return {
                    title: `Your ${terms.policy.singularTitle} Information`,
                    subtitle: `Welcome to your Staff Absence Portal. Here you can view your ${terms.policy.singularLower} details.`,
                }
            case '/sai/quotes':
                return {
                    title: 'Your new quotes are here.',
                    subtitle: 'Please review your quotes for the 2025-2026 academic year.',
                }
            case '/sai/dashboard':
            default:
                return {
                    title: `Manage your Staff Absence ${terms.policy.singularTitle}`,
                    subtitle: `Welcome to your Staff Absence Portal. Here you can manage your ${terms.policy.singularLower} and administer ${terms.claim.pluralLower}.`,
                }
        }
    }

    const pageHeader = getPageHeader()

    return (
        <div className="w-full min-h-[65vh]">
            <div className="flex max-w-[90rem] mx-auto lg:space-x-6">
                {!claimFormPage && <div aria-hidden="true" className="hidden lg:block lg:w-1/5" />}
                <div className="w-full">
                    <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-4">
                        {pageHeader.title}
                    </h3>
                    <h4 className="text-lg font-medium text-white text-center">
                        {pageHeader.subtitle}
                    </h4>
                </div>
            </div>
            <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6 mt-8">
                {!claimFormPage && <SideBar onNavigate={handleNavigate} />}
                <div id={editingClaim ? 'claim' : undefined} className={`w-full bg-white rounded-xl shadow-md ${creatingClaim ? 'p-6' : 'px-4 py-4'}`}>
                    {claimFormPage && (
                        <Link
                            to="/sai/claims"
                            className="mb-4 inline-flex items-center p-6 text-sm font-medium text-sas"
                        >
                            Back to Claims
                        </Link>
                    )}
                    {renderContent()}
                </div>
            </div>
        </div>
    )
}

export default SAIContainer
